import { useEffect, useState } from "react";

import { type BaseRecord, type HttpError, useShow } from "@refinedev/core";
import { useDocumentTitle } from "@refinedev/react-router";
import { z } from "zod";

/**
 * Shared show-page data hook. `useShow` -> dataProvider.getOne returns the flat
 * full DTO (the admin getOne endpoint, not the Typesense `IHit` wrapper used by
 * lists), so `query.data` is the record itself.
 *
 * The payload is validated with a lenient view schema. On a parse miss we still
 * surface the raw record (so a backend field rename never blanks the page) and
 * log the issue for follow-up.
 */
export function useShowRecord<T>(
  schema: z.ZodType<T>,
  title?: (record: T) => string,
) {
  const {
    query: { data: raw, isLoading },
  } = useShow<BaseRecord, HttpError>();
  const setTitle = useDocumentTitle();
  const [record, setRecord] = useState<T>();

  useEffect(() => {
    if (raw === undefined || raw === null) return;
    const parsed = schema.safeParse(raw);
    if (parsed.success) {
      setRecord(parsed.data);
      if (title) setTitle(title(parsed.data));
    } else {
      console.error("Show view schema mismatch", parsed.error);
      setRecord(raw as T);
    }
  }, [raw]);

  return { record, raw, isLoading };
}
